import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import ContactNote from 'types/ContactNote';
import { GlobalState } from 'reducers/stateTypes';
import { setCnError, setNoteDisplayDialog, toggleContactNoteDrawer } from 'reducers/contactNoteListReducer';
import { User } from 'types/User';
import { DisplayDialogs } from 'views/ContactNotes/ContactNotesContainer';
import { ContactNoteStatus } from 'views/ContactNoteForm/data/statusOptions';
import { EnvConfig } from 'config/envConfig';
import { selectIsUsedInBr } from 'selectors/contactNoteListSelectors';
import contactNoteDialogService from 'services/contactNoteDialogService';
import {
  ZfDrawer,
  ZfDrawerContent,
  ZfDrawerHeader,
  ZfStack,
  ZfTextButton,
  ZfDrawerFooter,
  ZfTypography,
  ZfDivider,
  ZfPopover,
  ZfIcon,
  ZfBox,
  ZfLink,
  ZfIconButton,
} from '@zafire/react';
import { initContactNotes } from 'reducers/contactNoteReducer';
import { ContactNoteDialogStatus } from 'views/ContactNoteFormDialog/zafire/components/data/ContactNoteDialogStatus';
import { base64ToArrayBuffer } from 'utils/arrayBufferUtil';
import {
  FileInfo,
  FileObjectWithDescription,
  AttachmentSource,
} from 'views/ContactNoteForm/components/DocumentUpload/DocumentDropZone';
import {
  CN_MGMT_API_URL,
  DOCUMENT_COUNTRY_GUIDELINES,
  DOCUMENT_JBG_2048_00,
  DOCUMENT_JBG_2038_00,
  DOCUMENT_JBG_2082_00,
} from 'services/constants';

import { openModal } from '../../../reducers/modalReducer';
import { DeleteNoteDialog } from './DeleteNoteDialog';
import ContactNoteAuditHistory from '../Components/ContactNoteAuditHistory';
import AttendeeBRView from './AttendeeBRView';

const NoteDetails = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const user: User = useSelector((state: GlobalState) => state.user.userDetails);
  const contactNote: ContactNote | null = useSelector((state: GlobalState) => state.contactNoteList.shownNote);
  const contactTypeOptions = useSelector((state: GlobalState) => state.resources.contactTypeOptions);
  const initiatedByOptions = useSelector((state: GlobalState) => state.resources.initiatedByOptions);
  const countryOptions = useSelector((state: GlobalState) => state.resources.countries);
  const topicOptions = useSelector((state: GlobalState) => state.resources.topicsOptions);
  const noteDrawerOpen: boolean = useSelector((state: GlobalState) => state.contactNoteList.drawerOpen);

  const { t } = useTranslation();
  const usedInBr = useSelector(selectIsUsedInBr);
  const dateRange: any[] = useSelector((state: GlobalState) => state.contactNoteList.dateRange);
  const subjectSearch: string = useSelector((state: GlobalState) => state.contactNoteList.subjectSearch);

  const viewAttachment = ({ fileObject }: { fileObject: FileObjectWithDescription | FileInfo }) => {
    const isEmailAttachment = Object.prototype.hasOwnProperty.call(fileObject, 'id') && !(fileObject as FileInfo).id;
    if (isEmailAttachment) {
      const blob = new Blob([base64ToArrayBuffer((fileObject as FileInfo).b64Data)], {
        type: (fileObject as FileInfo).mimeType,
      });
      const blobURL = URL.createObjectURL(blob);
      window.open(blobURL);
    } else {
      const attachmentSource = (fileObject as FileInfo).attachmentSource;
      const url = `${CN_MGMT_API_URL}/frontend/notes/attachments/${(fileObject as FileInfo).id}${
        attachmentSource === AttachmentSource.ONDEMAND && contactNote?.source === 'EAO' ? '?isEmail=true' : ''
      }`;

      if (fileObject.description?.endsWith(`.pdf`)) {
        window.open(url);
      } else {
        const link = document.createElement('a');
        link.download = fileObject.description as string;
        link.href = url;
        link.click();
      }
    }
  };

  const contactTypeLabel = useMemo(() => {
    if (contactNote && contactTypeOptions) {
      const label = contactTypeOptions.find((item) => item.value === contactNote.contactType)?.label;

      if (!label && contactNote?.contactType) {
        return contactNote?.contactType;
      }

      if (!label && !contactNote?.contactType) {
        return '-';
      }
      return label;
    }
    return '-';
  }, [contactTypeOptions, contactNote]);

  const topicLabel = useMemo(() => {
    if (contactNote && topicOptions) {
      const label = contactNote.topics.map((item) => {
        const res = topicOptions.find((obj) => obj.value === item)?.label;
        if (res) {
          return res;
        }
        return item;
      });
      return label.join(', ');
    }
    return '-';
  }, [topicOptions, contactNote]);

  const dateObject = useMemo(() => {
    if (contactNote) {
      const { startDateTime, endDateTime, isTimeAllowed } = contactNote;
      let startDate = '-',
        startTime = '-',
        endDate = '-',
        endTime = '-';
      if (startDateTime) {
        const parsedStart = parseISO(startDateTime);
        startDate = format(parsedStart, 'dd.MM.yyyy');
        if (!isTimeAllowed) {
          startTime = format(parsedStart, 'hh:mm a');
        }
      }

      if (endDateTime) {
        const parsedEnd = parseISO(endDateTime);
        endDate = format(parsedEnd, 'dd.MM.yyyy');
        endTime = format(parsedEnd, 'hh:mm a');
      }
      return { startDate, startTime, endDate, endTime };
    }
    return { startDate: '', startTime: '', endDate: '', endTime: '' };
  }, [contactNote]);

  const initiatedByLabel = useMemo(() => {
    if (contactNote && initiatedByOptions) {
      if (contactNote.initiatedBy === 'OTHER') {
        return 'Other';
      }
      const label = initiatedByOptions.find((item) => item.value === contactNote.initiatedBy)?.label;
      if (!label && !contactNote.initiatedBy) {
        return '-';
      }
      if (!label && contactNote.initiatedBy) {
        return contactNote.initiatedBy;
      }
      return label;
    }
    return '-';
  }, [contactNote, initiatedByOptions]);

  const countryLabel = useMemo(() => {
    if (contactNote && countryOptions) {
      const label = countryOptions.find((item) => item.value === contactNote.locationCountry)?.label;
      return label || '-';
    }
    return '-';
  }, [countryOptions, contactNote]);

  const uploadedDocuments =
    contactNote?.attachments.length === 0 ? (
      '-'
    ) : (
      <ZfStack spacing="extra-extra-small">
        {contactNote?.attachments.map((item, index) => (
          <ZfStack key={`upload_${item.name}_${index}`} direction="row" spacing="extra-extra-small">
            <ZfIcon size="medium" name="attach_file" />
            <ZfTypography style={{ flex: 1 }}>{item.name}</ZfTypography>
            <ZfIconButton
              size="medium"
              icon="download"
              hierarchy="tertiary"
              onClick={() => viewAttachment({ fileObject: item })}
            />
          </ZfStack>
        ))}
      </ZfStack>
    );

  const { techId } = useParams<{ techId: string }>();
  const [showDialog, setShowDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteNote = useCallback(async () => {
    if (contactNote?.id) {
      try {
        setIsDeleting(true);

        await contactNoteDialogService.deleteContactNote(contactNote?.id);
        if (EnvConfig.config.useAgGridV2HomeSearch) {
          queryClient.invalidateQueries(['searchNotesAgGridV2']);
        } else {
          const notes = await contactNoteDialogService.getMyNotes(
            '',
            false,
            subjectSearch,
            dateRange,
            '',
            techId,
            usedInBr,
          );
          dispatch(initContactNotes(notes));
        }
        dispatch(toggleContactNoteDrawer(false));
        setShowDialog(false);
      } catch (error: any) {
        if (error && error.response && error.response.status === 404) {
          dispatch(setCnError(t('contactNote.search.noContactNote')));
        } else {
          dispatch(
            openModal({
              modalType: 'ErrorDialog',
              modalProps: { error },
            }),
          );
        }
        setShowDialog(false);
      } finally {
        setIsDeleting(false);
      }
    }
  }, [contactNote?.id, dateRange, techId, usedInBr, initContactNotes, EnvConfig.config.useAgGridV2HomeSearch]);

  const renderRightButton = useMemo(() => {
    if (contactNote?.status === ContactNoteDialogStatus.SUBMITTED && !user.hasUpdateQualifiedContact) {
      return (
        <ZfTextButton size="medium" hierarchy="secondary" onClick={() => dispatch(toggleContactNoteDrawer(false))}>
          {t('noteform.footer.close', 'Close')}
        </ZfTextButton>
      );
    }
    return (
      <>
        <ZfTextButton size="medium" hierarchy="tertiary" onClick={() => dispatch(toggleContactNoteDrawer(false))}>
          {t('noteform.footer.close', 'Close')}
        </ZfTextButton>
        <ZfTextButton
          size="medium"
          hierarchy="secondary"
          onClick={() =>
            user.hasNoteEditAccess || user.hasUpdateQualifiedContact
              ? dispatch(setNoteDisplayDialog(DisplayDialogs.EDIT_VIEW))
              : {}
          }
          disabled={
            user.hasUpdateQualifiedContact
              ? false
              : (contactNote as ContactNote)?.status === ContactNoteStatus.SUBMITTED || !user.hasNoteEditAccess
          }
        >
          {t('noteform.footer.edit', 'Edit')}
        </ZfTextButton>
      </>
    );
  }, [
    user.hasNoteDeleteAccess,
    user.hasUpdateQualifiedContact,
    user.hasNoteEditAccess,
    contactNote?.status,
    EnvConfig.config.createContactNoteNewUiEnabled,
  ]);

  const renderLeftButton = useMemo(() => {
    return (
      (user.hasNoteDeleteAccess ||
        (contactNote?.status === ContactNoteDialogStatus.DRAFT && user.hasNoteEditAccess)) && (
        <ZfTextButton
          slot="secondary"
          kind="destructive"
          size="medium"
          hierarchy="secondary"
          onClick={() => setShowDialog(true)}
        >
          {t('noteform.footer.delete', 'Delete')}
        </ZfTextButton>
      )
    );
  }, [
    user.hasNoteDeleteAccess,
    user.hasNoteEditAccess,
    contactNote?.status,
    EnvConfig.config.createContactNoteNewUiEnabled,
  ]);

  return (
    <>
      <ZfDrawer
        overlay={false}
        backdrop={false}
        show={noteDrawerOpen}
        onDrawerClose={() => {
          dispatch(toggleContactNoteDrawer(false));
        }}
        style={{ border: 'var(--border-weight-px, 1px) solid var(--border-color-default-emphasis, #D5D6E0)' }}
      >
        <ZfDrawerHeader showCloseButton={false}>{t('contactNote.form.contactNoteDetails')}</ZfDrawerHeader>
        {contactNote && (
          <ZfDrawerContent style={{ width: `calc(50vw - 8px)`, backgroundColor: 'var(--bg-color-default-muted)' }}>
            <ZfBox styles={{ overflowY: 'auto', marginRight: '-20px', paddingRight: '5px' }}>
              <ZfStack spacing="medium">
                <ZfStack spacing="extra-extra-small">
                  <ZfTypography color="normal">{t('contactNote.form.subject')}</ZfTypography>
                  <ZfTypography color="emphasis" style={{ overflowWrap: 'break-word' }}>
                    {contactNote.subject}
                  </ZfTypography>
                </ZfStack>
                <ZfStack spacing="extra-extra-small">
                  <ZfStack spacing="extra-extra-small" direction="row">
                    <ZfTypography color="normal">{t('contactNote.form.notes')}</ZfTypography>
                    <ZfPopover>
                      <ZfIcon slot="anchor" size="medium" name="info" style={{ cursor: 'pointer' }}></ZfIcon>
                      <ZfBox styles={{ textWrap: 'wrap' }}>
                        <ZfTypography style={{ whiteSpace: 'pre-line' }} variant="label-tiny" color="normal">
                          <Trans
                            i18nKey="contactNote.form.popover.notes.label"
                            components={{
                              u: <u />,
                              strong: <ZfTypography variant="label-small" weight="bold" />,
                              1: (
                                <ZfLink
                                  size="extra-small"
                                  type="inline"
                                  target="_blank"
                                  rel="noreferrer"
                                  href={DOCUMENT_JBG_2038_00}
                                />
                              ),
                              2: (
                                <ZfLink
                                  size="extra-small"
                                  type="inline"
                                  target="_blank"
                                  rel="noreferrer"
                                  href={DOCUMENT_COUNTRY_GUIDELINES}
                                />
                              ),
                              3: (
                                <ZfLink
                                  size="extra-small"
                                  type="inline"
                                  target="_blank"
                                  rel="noreferrer"
                                  href={DOCUMENT_JBG_2048_00}
                                />
                              ),
                            }}
                          ></Trans>
                        </ZfTypography>
                      </ZfBox>
                    </ZfPopover>
                  </ZfStack>
                  <ZfTypography color="emphasis" style={{ overflowWrap: 'break-word', whiteSpace: 'pre-line' }}>
                    {contactNote.notes}
                  </ZfTypography>
                </ZfStack>

                <AttendeeBRView />
                <ZfStack direction="row" spacing="small">
                  <ZfTypography variant="label-medium" color="emphasis" weight="bold">
                    {t('contactNote.form.contactDetail')}
                  </ZfTypography>
                  <ZfDivider style={{ flex: 1, paddingTop: 'var(--padding-top-sm)' }} />
                </ZfStack>
                <ZfStack direction="row" spacing="small">
                  <ZfStack direction="column" spacing="extra-extra-small" style={{ flex: 1 }}>
                    <ZfStack spacing="extra-extra-small" direction="row">
                      <ZfTypography color="normal">{t('contactNote.form.communicationChannel')}</ZfTypography>
                      <ZfPopover>
                        <ZfIcon slot="anchor" size="medium" name="info" style={{ cursor: 'pointer' }}></ZfIcon>
                        <ZfBox styles={{ textWrap: 'wrap' }}>
                          <ZfTypography variant="label-tiny" color="normal">
                            <Trans
                              i18nKey="contactNote.form.helperText.communicationChannel"
                              components={{
                                1: (
                                  <ZfLink
                                    size="extra-small"
                                    type="inline"
                                    target="_blank"
                                    rel="noreferrer"
                                    href={DOCUMENT_JBG_2082_00}
                                  />
                                ),
                              }}
                            />
                          </ZfTypography>
                        </ZfBox>
                      </ZfPopover>
                    </ZfStack>
                    <ZfTypography color="emphasis">{contactTypeLabel}</ZfTypography>
                  </ZfStack>
                  <ZfStack direction="column" spacing="extra-extra-small" style={{ flex: 1 }}>
                    <ZfStack direction="column" spacing="extra-extra-small" style={{ flex: 1 }}>
                      <ZfTypography color="normal">{t('contactNote.form.topics')}</ZfTypography>
                      <ZfTypography color="emphasis">{topicLabel || '-'}</ZfTypography>
                    </ZfStack>
                  </ZfStack>
                </ZfStack>
                {(contactNote?.topicOther || contactNote.contactTypeOther) && (
                  <ZfStack direction="row" spacing="small">
                    <ZfStack
                      direction="column"
                      spacing="extra-extra-small"
                      style={{ flex: 1, overflowWrap: 'anywhere' }}
                    >
                      {contactNote.contactType === 'OTHER' && (
                        <ZfTypography color="normal">{t('contactNote.form.communicationChannelOther')}</ZfTypography>
                      )}
                      {contactNote.contactType === 'OTHER' && (
                        <ZfTypography color="emphasis">{contactNote.contactTypeOther || '-'}</ZfTypography>
                      )}
                    </ZfStack>
                    <ZfStack
                      direction="column"
                      spacing="extra-extra-small"
                      style={{ flex: 1, overflowWrap: 'anywhere' }}
                    >
                      {contactNote.topics.includes('OTHER') && (
                        <ZfTypography color="normal">{t('contactNote.form.topicsOther')}</ZfTypography>
                      )}
                      {contactNote.topics.includes('OTHER') && (
                        <ZfTypography color="emphasis">{contactNote.topicOther || '-'}</ZfTypography>
                      )}
                    </ZfStack>
                  </ZfStack>
                )}
              </ZfStack>
              <ZfStack direction="row" spacing="small">
                <ZfStack direction="column" spacing="extra-extra-small" style={{ flex: 1 }}>
                  <ZfTypography color="normal">
                    {contactNote.endDateTime ? t('contactNote.form.startDate') : t('contactNote.form.date')}
                  </ZfTypography>
                  <ZfTypography color="emphasis">{dateObject.startDate}</ZfTypography>
                </ZfStack>
                <ZfStack direction="column" spacing="extra-extra-small" style={{ flex: 1 }}>
                  <ZfTypography color="normal">
                    {contactNote.endDateTime ? t('contactNote.form.startTime') : t('contactNote.form.time')}
                  </ZfTypography>
                  <ZfTypography color="emphasis">{dateObject.startTime}</ZfTypography>
                </ZfStack>
                <ZfStack direction="column" spacing="extra-extra-small" style={{ flex: 1 }}>
                  <ZfTypography color="normal">{contactNote.endDateTime && t('contactNote.form.endDate')}</ZfTypography>
                  <ZfTypography color="emphasis">{contactNote.endDateTime && dateObject.endDate}</ZfTypography>
                </ZfStack>
                <ZfStack direction="column" spacing="extra-extra-small" style={{ flex: 1 }}>
                  <ZfTypography color="normal">{contactNote.endDateTime && t('contactNote.form.endTime')}</ZfTypography>
                  <ZfTypography color="emphasis">{contactNote.endDateTime && dateObject.endTime}</ZfTypography>
                </ZfStack>
              </ZfStack>
              <ZfStack direction="row" spacing="small">
                <ZfStack direction="column" spacing="extra-extra-small" style={{ flex: 1 }}>
                  <ZfTypography color="normal">{t('contactNote.form.initiatedBy')}</ZfTypography>
                  <ZfTypography color="emphasis">{initiatedByLabel}</ZfTypography>
                </ZfStack>
                <ZfStack direction="row" spacing="extra-extra-small" style={{ flex: 1 }}>
                  <ZfStack direction="column" spacing="extra-extra-small" style={{ flex: 1 }}>
                    <ZfTypography color="normal">{t('contactNote.form.qualifiedClientContact')}</ZfTypography>
                    <ZfTypography color="emphasis">
                      {contactNote.qualifiedClientContact === true
                        ? t('contactNote.form.yes')
                        : contactNote.qualifiedClientContact === false
                          ? t('contactNote.form.no')
                          : '-'}{' '}
                    </ZfTypography>
                  </ZfStack>
                  <ZfStack direction="column" spacing="extra-extra-small" style={{ flex: 1 }}>
                    <ZfStack spacing="extra-extra-small" direction="row">
                      <ZfTypography color="normal">{t('contactNote.form.crossBorderActivity')}</ZfTypography>
                      <ZfPopover>
                        <ZfIcon slot="anchor" size="medium" name="info" style={{ cursor: 'pointer' }}></ZfIcon>
                        <ZfBox>
                          <ZfTypography variant="label-tiny" color="normal" style={{ whiteSpace: 'pre-line' }}>
                            {t('contactNote.form.crossBorderActivityPopover', {
                              interpolation: { escapeValue: false },
                            })}
                          </ZfTypography>
                        </ZfBox>
                      </ZfPopover>
                    </ZfStack>

                    <ZfTypography color="emphasis">
                      {contactNote.crossBorderActivity === true
                        ? t('contactNote.form.yes')
                        : contactNote.crossBorderActivity === false
                          ? t('contactNote.form.no')
                          : '-'}{' '}
                    </ZfTypography>
                  </ZfStack>
                </ZfStack>
              </ZfStack>

              <ZfStack direction="row" spacing="small">
                <ZfStack direction="column" spacing="extra-extra-small" style={{ flex: 1 }}>
                  <ZfTypography color="normal">{t('contactNote.form.locationCountry')}</ZfTypography>
                  <ZfTypography color="emphasis">{countryLabel}</ZfTypography>
                </ZfStack>
                <ZfStack direction="row" spacing="extra-extra-small" style={{ flex: 1 }}>
                  <ZfStack direction="column" spacing="extra-extra-small" style={{ flex: 1 }}>
                    <ZfTypography color="normal">{t('contactNote.form.locationCity')}</ZfTypography>
                    <ZfTypography color="emphasis" style={{ overflowWrap: 'anywhere' }}>
                      {contactNote.locationCity || '-'}
                    </ZfTypography>
                  </ZfStack>
                  <ZfStack direction="column" spacing="extra-extra-small" style={{ flex: 1 }}>
                    <ZfTypography color="normal">{t('contactNote.form.tripId')}</ZfTypography>
                    <ZfTypography color="emphasis" style={{ overflowWrap: 'anywhere' }}>
                      {contactNote.tripId || '-'}
                    </ZfTypography>
                  </ZfStack>
                </ZfStack>
              </ZfStack>
              <ZfStack direction="row" spacing="small">
                <ZfTypography variant="label-medium" color="emphasis" weight="bold">
                  {t('contactNote.form.documentUpload')}
                </ZfTypography>
                <ZfDivider style={{ flex: 1, paddingTop: 'var(--padding-top-sm)' }} />
              </ZfStack>
              {uploadedDocuments}
              <ContactNoteAuditHistory contactNote={contactNote} />
            </ZfBox>
          </ZfDrawerContent>
        )}
        <ZfDrawerFooter>
          {renderLeftButton}
          <ZfStack slot="primary" direction="row" spacing="extra-small">
            {renderRightButton}
          </ZfStack>
        </ZfDrawerFooter>
      </ZfDrawer>
      <DeleteNoteDialog
        show={showDialog}
        onClose={() => setShowDialog(false)}
        onProceed={() => {
          handleDeleteNote();
        }}
        loading={isDeleting}
      />
    </>
  );
};

export default NoteDetails;
